import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../config/firebase";
import { doc, collection, addDoc } from "firebase/firestore";
import { useSubscription } from "../../../../hooks/useSubscription";

interface Props {
  className?: string;
  userID: string;
  onHowItWorks?: () => void;
  hasProjects: boolean;
  projectCount?: number;
}

const FEATURES = [
  {
    icon: "bi-magic",
    title: "Describe Your Idea",
    description: "Tell us your business concept in plain language",
  },
  {
    icon: "bi-robot",
    title: "AI Validates It",
    description: "Our AI populates a 24-step validation framework",
  },
  {
    icon: "bi-pencil-square",
    title: "You Refine It",
    description: "Review, edit, and make the analysis your own",
  },
  {
    icon: "bi-rocket-takeoff",
    title: "Launch Faster",
    description: "Generate logos, websites, MVPs, and marketing plans",
  },
];


export const OnboardingHero: React.FC<Props> = ({
  className = "",
  userID,
  onHowItWorks,
  hasProjects,
  projectCount = 0,
}) => {
  const navigate = useNavigate();
  const { canUseFeature, projectLimit, tier } = useSubscription();
  const [isVisible, setIsVisible] = useState(true);
  const [ideaText, setIdeaText] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAtProjectLimit = tier === "free" && projectCount >= projectLimit;

  useEffect(() => {
    const dismissed = localStorage.getItem("onboardingDismissed");
    if (dismissed && hasProjects) {
      setIsVisible(false);
    }
  }, [hasProjects]);

  const handleDismiss = () => {
    localStorage.setItem("onboardingDismissed", "true");
    setIsVisible(false);
  };

  const handleSubmit = async () => {
    const trimmed = ideaText.trim();
    if (!trimmed) return;

    // Enforce free-tier project limit
    if (isAtProjectLimit) {
      navigate("/profile/subscription?utm_source=project_limit");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const useAI = canUseFeature("ai_autofill") && trimmed.length >= 50;
      const userRef = doc(db, "users", userID);
      const projectsRef = collection(userRef, "projects");

      const projectDoc = await addDoc(projectsRef, {
        projectTitle: trimmed,
        projectDescription: trimmed,
        projectProgress: 0,
        aiGenerated: useAI,
        createdAt: new Date().toISOString(),
      });

      const projectID = projectDoc.id;

      if (useAI) {
        navigate("/ai-progress", {
          state: {
            userID,
            projectID,
            projectName: trimmed,
            projectDescription: trimmed,
          },
        });
      } else {
        navigate("/project", {
          state: {
            userID,
            projectID,
            projectTitle: trimmed,
            projectDescription: trimmed,
          },
        });
      }
    } catch (err) {
      console.error("Failed to create project:", err);
      setError("Failed to create project. Please try again.");
      setIsCreating(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`card ${className}`} style={{ overflow: "hidden" }}>
      {/* Split background */}
      <div
        className="card-body position-relative py-10 px-10"
        style={{
          background: "linear-gradient(to right, #7c8cf5 0%, #667eea 55%, #5a3d8a 70%, #4a2d7a 100%)",
        }}
      >
        {/* Dismiss button */}
        {hasProjects && (
          <button
            className="btn btn-icon btn-sm position-absolute"
            onClick={handleDismiss}
            style={{ top: "16px", right: "16px", background: "rgba(255,255,255,0.2)" }}
          >
            <i className="bi bi-x text-white fs-4"></i>
          </button>
        )}

        {/* Main content */}
        <div className="row align-items-center">
          <div className="col-lg-7">
            <div className="mb-6">
              <span className="badge badge-white text-primary fw-bold mb-4 px-4 py-2">
                AI-Powered Business Validation
              </span>
              <h1 className="text-white fw-bolder mb-4" style={{ fontSize: "2.2rem", lineHeight: "1.2" }}>
                Turn Your Business Idea Into a Validated Plan in Minutes
              </h1>
              <p className="text-white fs-5 mb-6" style={{ opacity: 0.85 }}>
                Describe your idea below. Our AI analyzes it through a proven 24-step framework,
                then you review and refine.
              </p>
            </div>

            {/* Inline prompt box */}
            <div className="mb-6">
              {isAtProjectLimit ? (
                /* Upgrade prompt — free tier at limit */
                <div
                  className="d-flex align-items-center rounded-3 gap-4"
                  style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", padding: "16px 20px" }}
                >
                  <i className="bi bi-lock-fill text-white flex-shrink-0" style={{ fontSize: 24 }}></i>
                  <div className="flex-grow-1">
                    <div className="text-white fw-bold fs-6 mb-1">
                      Free plan: 1 of 1 project used
                    </div>
                    <div className="text-white fs-7" style={{ opacity: 0.8 }}>
                      Upgrade to Pro to create unlimited projects and unlock AI features.
                    </div>
                  </div>
                  <button
                    className="btn btn-sm fw-bolder flex-shrink-0"
                    onClick={() => navigate("/profile/subscription?utm_source=project_limit")}
                    style={{ background: "white", color: "#5a3d8a", borderRadius: 8, padding: "8px 18px", fontSize: 13 }}
                  >
                    Upgrade to Pro
                  </button>
                </div>
              ) : (
                <>
                  <div
                    className="d-flex align-items-stretch rounded-3 overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.9)" }}
                  >
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent text-dark fs-5 px-5 py-4"
                      placeholder="Describe your business idea..."
                      value={ideaText}
                      onChange={(e) => setIdeaText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !isCreating && handleSubmit()}
                      disabled={isCreating}
                      style={{ outline: "none", boxShadow: "none" }}
                    />
                    <button
                      className="btn fw-bolder px-6 rounded-0 d-flex align-items-center gap-2 text-white"
                      onClick={handleSubmit}
                      disabled={!ideaText.trim() || isCreating}
                      style={{ background: "#5a3d8a", whiteSpace: "nowrap" }}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-right"></i>
                          Start Validating
                        </>
                      )}
                    </button>
                  </div>
                  {error && (
                    <div className="text-white mt-2 fs-7" style={{ opacity: 0.9 }}>
                      <i className="bi bi-exclamation-circle me-1 text-white"></i>
                      {error}
                    </div>
                  )}
                  {!hasProjects && onHowItWorks && (
                    <button
                      className="btn btn-link text-white fw-semibold mt-3 p-0 fs-7"
                      style={{ opacity: 0.7, textDecoration: "underline" }}
                      onClick={onHowItWorks}
                    >
                      <i className="bi bi-play-circle me-1 text-white"></i>
                      See How It Works
                    </button>
                  )}
                </>
              )}
            </div>

          </div>

          <div className="col-lg-5 d-none d-lg-block">
            <div className="ps-6">
              {FEATURES.map((feature, index) => (
                <div
                  key={feature.title}
                  className="d-flex align-items-center mb-4 p-4 rounded"
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(10px)",
                    animation: `fadeInRight 0.5s ease ${index * 0.15}s both`,
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded me-4"
                    style={{
                      width: "44px",
                      height: "44px",
                      minWidth: "44px",
                      background: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <i className={`bi ${feature.icon} text-white fs-3`}></i>
                  </div>
                  <div>
                    <div className="d-flex align-items-center">
                      <span
                        className="badge badge-circle me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          fontSize: "10px",
                          background: "rgba(255,255,255,0.3)",
                          color: "white",
                        }}
                      >
                        {index + 1}
                      </span>
                      <span className="text-white fw-bold fs-6">{feature.title}</span>
                    </div>
                    <span className="text-white fs-7" style={{ opacity: 0.75 }}>
                      {feature.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder styles for input */}
      <style>{`
        .form-control.bg-transparent::placeholder {
          color: rgba(0, 0, 0, 0.45);
        }
      `}</style>
    </div>
  );
};
